import { useMemo } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { appModules } from "../../modules";
import type { SettingsSection } from "../../modules/types";
import { cx } from "../../ui/cx";
import { Icon } from "../../ui/Icon";
import { IconButton } from "../../ui/IconButton";
import { mergeSettingsSections } from "../nav";
import { isWorkspaceAdmin, useMe } from "../session";
import { TopBar } from "../shell/TopBar";
import { s } from "../strings";
import styles from "./FullPageSettingsLayout.module.css";

function SectionPlaceholder() {
  return <p className={styles.placeholder}>{s.settingsPending}</p>;
}

/**
 * Workspace settings: keeps the top bar, hides the app sidebar, and shows its own hash-section nav
 * (docs/reference/live/settings-workspace-general.md).
 */
export function FullPageSettingsLayout() {
  const me = useMe();
  const { workspaceId = "" } = useParams();
  const { hash, key: locationKey } = useLocation();
  const navigate = useNavigate();
  const sections = useMemo(() => mergeSettingsSections(appModules, SectionPlaceholder), []);
  const workspace = me.workspaces.find((w) => w.id === workspaceId);

  if (!workspace) return <Navigate to="/404" replace />;
  if (!isWorkspaceAdmin(workspace)) return <Navigate to="/" replace />;

  const current = sections.find((sec) => `#${sec.hash}` === hash) ?? sections[0];
  if (!current) return null;

  const goBack = () => {
    // "default" is the key of the first entry in this tab's history: there is nowhere in-app to go back to.
    if (locationKey !== "default" && window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  let prevGroup: SettingsSection["group"] | null = null;
  return (
    <div className={styles.page}>
      <TopBar />
      <div className={styles.subHeader}>
        <IconButton icon="arrow-left" label={s.back} appearance="outlined" onClick={goBack} />
        <span className={styles.title}>
          <Icon name="settings" size={18} />
          {s.workspaceSettings}
        </span>
      </div>
      <div className={styles.body}>
        <nav className={styles.nav} aria-label={s.workspaceSettings}>
          {sections.map((sec) => {
            const gap = prevGroup !== null && prevGroup !== sec.group;
            prevGroup = sec.group;
            const active = sec.hash === current.hash;
            return (
              <a
                key={sec.hash}
                href={`#${sec.hash}`}
                className={cx(styles.navItem, gap && styles.groupStart, active && styles.navActive)}
                aria-current={active ? "page" : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  navigate({ hash: sec.hash }, { replace: true });
                }}
              >
                {sec.label}
              </a>
            );
          })}
        </nav>
        <section className={styles.content} aria-labelledby="settings-section-title">
          <h1 id="settings-section-title" className={styles.sectionTitle}>
            {current.label}
          </h1>
          <current.Component />
        </section>
      </div>
    </div>
  );
}
